import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SpaceType } from '@/__generated__/types';
import {
  DEFAULT_TIMESPAN,
  type DashboardPeriod,
  isDashboardPeriod,
} from '@/constants/dashboard-period';
import { useSpaces } from '@/hooks/useSpaces';

const PARAM = {
  spaceId: 'spaceId',
  spaceType: 'spaceType',
  timeSpan: 'timeSpan',
} as const;

export interface DashboardScope {
  spaceId?: string;
  spaceType?: SpaceType;
  timeSpan: DashboardPeriod;
  selectSpace: (spaceId: string) => void;
  setTimeSpan: (timeSpan: DashboardPeriod) => void;
}

/**
 * The dashboard's current scope (selected space + period), stored in the URL
 * query params so it is shareable via link and shared with the app-wide AI
 * assistant without a provider: both the dashboard filters and the chat read
 * the same params, and missing params fall back to the dashboard defaults.
 */
export const useDashboardScope = (): DashboardScope => {
  const [params, setParams] = useSearchParams();
  const { spaces } = useSpaces();

  // The root node (whole portfolio) unless the URL names a space.
  const defaultSpaceId =
    spaces.find((space) => space.parentId === null)?.id ?? spaces[0]?.id;

  const spaceId = params.get(PARAM.spaceId) ?? defaultSpaceId;
  const spaceType =
    (params.get(PARAM.spaceType) as SpaceType | null) ??
    spaces.find((space) => space.id === spaceId)?.type;

  const rawTimeSpan = params.get(PARAM.timeSpan);
  const timeSpan = isDashboardPeriod(rawTimeSpan)
    ? rawTimeSpan
    : DEFAULT_TIMESPAN;

  const selectSpace = useCallback(
    (id: string) => {
      const type = spaces.find((space) => space.id === id)?.type;
      setParams(
        (prev) => {
          prev.set(PARAM.spaceId, id);
          if (type) {
            prev.set(PARAM.spaceType, type);
          }
          return prev;
        },
        { replace: true },
      );
    },
    [setParams, spaces],
  );

  const setTimeSpan = useCallback(
    (value: DashboardPeriod) => {
      setParams(
        (prev) => {
          prev.set(PARAM.timeSpan, value);
          return prev;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return { spaceId, spaceType, timeSpan, selectSpace, setTimeSpan };
};
